// "Import from PDF": reads a 1099 in the browser and offers what it found as pre-filled values for the editor.
// Nothing leaves the device, nothing is saved here, and a password for a locked PDF is used once and dropped.
import { useEffect, useRef, useState, type ReactNode } from "react";
import type { Amount, Source } from "@ftr/tax-core";
import { candidateFingerprint, FORM_NAME, parse1099, type FormCandidate, type Found, type ParsedDocument } from "../import/parse1099.ts";
import type { AskPassword, PasswordReason } from "../import/pdf.ts";
import { useOrganizer } from "./context.tsx";
import { AmountText } from "./fields.tsx";

export interface ImportedFile {
  hash: string;
  name: string;
}

type Want = "form_1099b" | "payer";

type Stage =
  | { kind: "pick" }
  | { kind: "working"; what: string }
  | { kind: "password"; reason: PasswordReason }
  | { kind: "error"; message: string }
  | { kind: "done"; parsed: ParsedDocument; file: ImportedFile; candidates: FormCandidate[]; already: Map<string, Source>; sameFile: Source[] };

const WANTED: Record<Want, FormCandidate["kind"][]> = {
  form_1099b: ["form_1099b"],
  payer: ["form_1099nec", "form_1099misc", "form_1099k"],
};

export function ImportPanel({ want, onUse, onClose }: { want: Want; onUse: (c: FormCandidate, file: ImportedFile) => void; onClose: () => void }) {
  const { yf } = useOrganizer();
  const [stage, setStage] = useState<Stage>({ kind: "pick" });
  const [password, setPassword] = useState("");
  const answer = useRef<((pw: string | null) => void) | null>(null);
  const alive = useRef(true);
  useEffect(
    () => () => {
      alive.current = false;
      answer.current?.(null);
    },
    [],
  );

  const askPassword: AskPassword = (reason) =>
    new Promise((resolve) => {
      answer.current = resolve;
      setStage({ kind: "password", reason });
    });

  const submitPassword = () => {
    const pw = password;
    setPassword("");
    setStage({ kind: "working", what: "Unlocking the PDF in your browser…" });
    answer.current?.(pw);
    answer.current = null;
  };

  const cancelPassword = () => {
    setPassword("");
    answer.current?.(null);
    answer.current = null;
    setStage({ kind: "pick" });
  };

  const start = async (file: File) => {
    setStage({ kind: "working", what: `Reading ${file.name} on this device…` });
    try {
      const bytes = await file.arrayBuffer();
      const pdf = await import("../import/pdf.ts");
      const hash = await pdf.sha256Hex(bytes);
      const sameFile = yf.sources.filter((s) => s.deletedAt === null && s.fileHash === hash);
      let items;
      try {
        items = await pdf.extractText(bytes, askPassword);
      } catch (e) {
        if (!alive.current) return;
        if (e instanceof pdf.PdfCancelled) return setStage({ kind: "pick" });
        return setStage({ kind: "error", message: pdf.describePdfError(e) });
      }
      if (!alive.current) return;
      const parsed = parse1099(items);
      const candidates = parsed.forms.filter((f) => WANTED[want].includes(f.kind));
      const already = new Map<string, Source>();
      for (const c of candidates) {
        const fp = candidateFingerprint(hash, c);
        const s = yf.sources.find((x) => x.deletedAt === null && x.fingerprint === fp);
        if (s) already.set(fp, s);
      }
      const fresh = candidates.filter((c) => !already.has(candidateFingerprint(hash, c)));
      if (candidates.length === 1 && fresh.length === 1 && sameFile.length === 0) return onUse(fresh[0]!, { hash, name: file.name });
      setStage({ kind: "done", parsed, file: { hash, name: file.name }, candidates, already, sameFile });
    } catch {
      if (alive.current) setStage({ kind: "error", message: "The file couldn't be read." });
    }
  };

  return (
    <div className="import-panel" role="group" aria-label="Import from PDF">
      <p className="import-title">Import from a 1099 PDF</p>
      <p className="field-hint">
        The PDF is read on this device and never uploaded. What's found only fills in the boxes below; check each one against the form before you save.
      </p>

      {stage.kind === "pick" && (
        <div className="import-row">
          <label className="btn btn--outline btn--sm import-file">
            Choose PDF
            <input
              type="file"
              accept="application/pdf,.pdf"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) void start(f);
              }}
            />
          </label>
          <button type="button" className="btn btn--plain btn--sm" onClick={onClose}>
            Cancel
          </button>
        </div>
      )}

      {stage.kind === "working" && <p className="muted small">{stage.what}</p>}

      {stage.kind === "password" && (
        <form
          className="import-password"
          onSubmit={(e) => {
            e.preventDefault();
            submitPassword();
          }}
        >
          <label className="field">
            <span className="field-label">{stage.reason === "incorrect" ? "That password didn't open it. Try again." : "This PDF has a password."}</span>
            <input className="text-input" type="password" autoComplete="off" autoFocus value={password} onChange={(e) => setPassword(e.target.value)} />
            <span className="field-hint">Brokers often use your Social Security number without dashes. The password is used once here and not kept.</span>
          </label>
          <div className="import-row">
            <button type="submit" className="btn btn--primary btn--sm" disabled={!password}>
              Unlock
            </button>
            <button type="button" className="btn btn--plain btn--sm" onClick={cancelPassword}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {stage.kind === "error" && (
        <>
          <p className="field-msg">{stage.message}</p>
          <div className="import-row">
            <button type="button" className="btn btn--outline btn--sm" onClick={() => setStage({ kind: "pick" })}>
              Try another file
            </button>
            <button type="button" className="btn btn--plain btn--sm" onClick={onClose}>
              Enter by hand
            </button>
          </div>
        </>
      )}

      {stage.kind === "done" && <Results stage={stage} want={want} onUse={onUse} onPick={() => setStage({ kind: "pick" })} onClose={onClose} />}
    </div>
  );
}

function Results({ stage, want, onUse, onPick, onClose }: { stage: Extract<Stage, { kind: "done" }>; want: Want; onUse: (c: FormCandidate, f: ImportedFile) => void; onPick: () => void; onClose: () => void }) {
  const { parsed, file, candidates, already, sameFile } = stage;
  const allAdded = candidates.length > 0 && candidates.every((c) => already.has(candidateFingerprint(file.hash, c)));
  const wantName = want === "form_1099b" ? "1099-B boxes 8–11" : "a 1099-NEC, 1099-MISC or 1099-K income box";
  return (
    <>
      {allAdded && (
        <p className="callout callout--warn">
          <strong>Already imported.</strong> {file.name} was imported before{sameFile[0] ? ` on ${new Date(sameFile[0].addedAt).toLocaleDateString()}` : ""}, and every form in it is already in your tax report. Nothing was added.
        </p>
      )}
      {!allAdded && sameFile.length > 0 && <p className="callout callout--warn">This file was imported before. Forms already in your tax report are marked below and can't be added twice.</p>}
      {!parsed.hasText && <p className="field-msg">This PDF has no readable text, so it's probably a scan. Enter the boxes by hand from the form.</p>}
      {parsed.hasText && candidates.length === 0 && (
        <p className="field-msg">
          No {wantName} found in this PDF.{parsed.formsSeen.length ? ` It mentions ${parsed.formsSeen.join(", ")}.` : ""} Enter the boxes by hand from the form.
        </p>
      )}
      {parsed.notes.map((n) => (
        <p key={n} className="field-hint">
          {n}
        </p>
      ))}
      {candidates.length > 0 && (
        <ul className="import-list">
          {candidates.map((c, i) => {
            const fp = candidateFingerprint(file.hash, c);
            const added = already.get(fp);
            return (
              <li key={fp + i} className="import-item">
                <div className="row-main">
                  <p className="row-title">
                    {FORM_NAME[c.kind]}
                    {c.kind === "form_1099b" && <> · {c.description.found ? c.description.value : <span className="muted">box 1a not found</span>}</>}
                    {c.corrected.found && c.corrected.value && <span className="pill pill--warn">CORRECTED</span>}
                    {added && <span className="pill pill--muted">Already added</span>}
                  </p>
                  <p className="row-sub">
                    {c.kind === "form_1099b" ? (
                      <>
                        Box 8 <Amt f={c.box8} /> · Box 9 <Amt f={c.box9} /> · Box 10 <Amt f={c.box10} /> · Box 11 <Amt f={c.box11} />
                      </>
                    ) : (
                      <>
                        Income box <Amt f={c.incomeBox} /> · Box 4 <Amt f={c.box4} />
                      </>
                    )}
                    {" · "}
                    {c.issuer.found ? c.issuer.value : <span className="muted">payer not found</span>} · page {c.pages.join(", ")}
                  </p>
                </div>
                {!added && (
                  <div className="row-actions">
                    <button type="button" className="btn btn--outline btn--sm" onClick={() => onUse(c, file)}>
                      Use this one
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <div className="import-row">
        <button type="button" className="btn btn--outline btn--sm" onClick={onPick}>
          Choose another PDF
        </button>
        <button type="button" className="btn btn--plain btn--sm" onClick={onClose}>
          {allAdded || candidates.length === 0 ? "Close" : "Enter by hand instead"}
        </button>
      </div>
    </>
  );
}

function Amt({ f }: { f: Found<Amount> }) {
  if (f.found) return <AmountText a={f.value} />;
  return <span className="muted">{f.reason === "ambiguous" ? "unclear" : "not found"}</span>;
}

/** The hint under a pre-filled field: what the PDF said, or that nothing was found there. */
export function importHint(f: Found<unknown> | undefined, blankMeans = "leave it empty if the box is empty on the form"): ReactNode {
  if (!f) return null;
  if (f.found) {
    const shown = typeof f.value === "boolean" ? f.raw : f.raw;
    return (
      <span className="from-pdf">
        Read from your PDF{shown ? <> as “{shown}”</> : null}
        {f.page ? ` on page ${f.page}` : ""}. Check it against the form.
      </span>
    );
  }
  if (f.reason === "ambiguous") return <span className="from-pdf from-pdf--warn">More than one amount was found here in the PDF ({f.raw}). Copy the right one from the form.</span>;
  return <span className="from-pdf from-pdf--warn">Not found in the PDF. Copy it from the form, or {blankMeans}.</span>;
}

/** The banner above a pre-filled editor. */
export function ImportedBanner({ file, taxYear, organizerYear, onClear }: { file: ImportedFile; taxYear: Found<number>; organizerYear: number; onClear: () => void }) {
  return (
    <div className="callout">
      <p className="callout-line">
        <strong>Pre-filled from {file.name}.</strong> Nothing is saved until you check every box against the form and press Save.
      </p>
      {taxYear.found && taxYear.value !== organizerYear && (
        <p className="callout-line warn-text">
          The PDF says tax year {taxYear.value}, but this tax report is for {organizerYear}. It will be saved as a {taxYear.value} form and not counted here.
        </p>
      )}
      {!taxYear.found && <p className="callout-line">The tax year wasn't found in the PDF, so this is saved as a {organizerYear} form. Check the year on the form.</p>}
      <button type="button" className="link-btn" onClick={onClear}>
        Clear and enter by hand
      </button>
    </div>
  );
}
